import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  createContext,
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import translations from './useAlertContext.translations.json';

const { addTranslations, t } = i18nNsFactory('success-alert');
addTranslations(translations);

export type AlertType = 'success' | 'warning' | 'error';

export type AlertState = {
  type: AlertType;
  message: string;
  description?: ReactNode;
  timeout?: number; // in milliseconds
} | null;

export type AlertContextType = {
  alert: AlertState;
  show: (type: AlertType, message?: string) => void;
  setSuccess: (
    message?: string,
    description?: ReactNode,
    timeout?: number,
  ) => void;
  setWarning: (
    message?: string,
    description?: ReactNode,
    timeout?: number,
  ) => void;
  setError: (
    message?: string,
    description?: ReactNode,
    timeout?: number,
  ) => void;
  resetAlert: () => void;
};

export const AlertContext = createContext<AlertContextType | undefined>(
  undefined,
);

export const AlertProvider: FC<PropsWithChildren> = ({ children }) => {
  const [alert, setAlert] = useState<AlertState>(null);

  const defaultMessages: Record<AlertType, string> = useMemo(
    () => ({
      success: t('alert.saved'),
      warning: t('alert.warning'),
      error: t('alert.error'),
    }),
    [],
  );

  const show = useCallback(
    (
      type: AlertType,
      message?: string,
      description?: ReactNode,
      timeout?: number,
    ) => {
      setAlert({
        type,
        message: message ?? defaultMessages[type],
        description,
        timeout,
      });
    },
    [defaultMessages],
  );

  const setSuccess = useCallback(
    (message?: string, description?: ReactNode, timeout?: number) => {
      show('success', message, description, timeout);
    },
    [show],
  );

  const setWarning = useCallback(
    (message?: string, description?: ReactNode, timeout?: number) => {
      show('warning', message, description, timeout);
    },
    [show],
  );

  const setError = useCallback(
    (message?: string, description?: ReactNode, timeout?: number) => {
      show('error', message, description, timeout);
    },
    [show],
  );

  const resetAlert = useCallback(() => setAlert(null), []);

  const value = useMemo(
    () => ({ alert, show, setSuccess, setWarning, setError, resetAlert }),
    [alert, show, setSuccess, setWarning, setError, resetAlert],
  );

  return (
    <AlertContext.Provider value={value}>{children}</AlertContext.Provider>
  );
};

export const useAlertContext = () => {
  const ctx = useContext(AlertContext);
  if (!ctx)
    throw new Error('useAlertContext must be used within <AlertProvider>');
  return ctx;
};
